import { Search, X } from 'lucide-react'
import { useEffect, useId, useState } from 'react'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  SelectSearch,
  type SelectSearchOption,
} from '@/components/ui/select-search'
import type {
  ClientLite,
  DriverLite,
} from '@/components/planning/planning-types'

import type { MissionDatePreset, MissionStatusFilter } from './mission-types'

/**
 * Admin-side filters for the missions listing.
 *
 * Same draft-vs-committed pattern as the planning filters:
 * controls bind to local state for the user's typing comfort,
 * and we commit changes to the parent on debounce / blur. The
 * search input goes through the 300 ms debounce; selects /
 * presets commit immediately because they don't suffer the same
 * fetch-per-keystroke pitfall.
 */

export interface MissionFiltersValue {
  preset: MissionDatePreset
  dateFrom: string
  dateTo: string
  driverId: string | null
  clientId: string | null
  status: MissionStatusFilter
  search: string
}

interface MissionFiltersProps {
  value: MissionFiltersValue
  onChange: (next: MissionFiltersValue) => void
  drivers: readonly DriverLite[]
  driversLoading?: boolean
  clients: readonly ClientLite[]
  clientsLoading?: boolean
}

const PRESETS: ReadonlyArray<{ id: MissionDatePreset; label: string }> = [
  { id: 'today', label: "Aujourd'hui" },
  { id: 'last7', label: '7 derniers jours' },
  { id: 'last30', label: '30 derniers jours' },
  { id: 'thisMonth', label: 'Ce mois' },
  { id: 'lastMonth', label: 'Mois dernier' },
  { id: 'custom', label: 'Personnalisé' },
]

const STATUSES: ReadonlyArray<{ id: MissionStatusFilter; label: string }> = [
  { id: 'all', label: 'Tous' },
  { id: 'notVerified', label: 'Non vérifiés' },
  { id: 'verified', label: 'Vérifiés' },
  { id: 'ready', label: 'Prêts à envoyer' },
]

export function MissionFilters({
  value,
  onChange,
  drivers,
  driversLoading = false,
  clients,
  clientsLoading = false,
}: MissionFiltersProps) {
  const presetId = useId()
  const driverFieldId = useId()
  const clientFieldId = useId()
  const statusFieldId = useId()
  const searchId = useId()

  // Debounced search — same pattern as planning filters.
  const [searchDraft, setSearchDraft] = useState(value.search)
  useEffect(() => {
    if (searchDraft === value.search) return
    const t = setTimeout(() => onChange({ ...value, search: searchDraft }), 300)
    return () => clearTimeout(t)
  }, [searchDraft, value, onChange])

  function handlePreset(preset: MissionDatePreset): void {
    if (preset === 'custom') {
      onChange({ ...value, preset })
      return
    }
    const range = computeMissionRange(preset)
    onChange({ ...value, preset, dateFrom: range.dateFrom, dateTo: range.dateTo })
  }

  const driverOptions: SelectSearchOption[] = drivers.map((d) => {
    const opt: SelectSearchOption = { value: d.id, label: d.name }
    if (d.email !== '') opt.detail = d.email
    return opt
  })

  const clientOptions: SelectSearchOption[] = clients.map((c) => {
    const opt: SelectSearchOption = { value: c.id, label: c.name }
    if (c.email !== null && c.email !== '') opt.detail = c.email
    return opt
  })

  return (
    <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
      <div className="sm:col-span-3 flex flex-col gap-1.5">
        <Label htmlFor={presetId}>Période</Label>
        <select
          id={presetId}
          value={value.preset}
          onChange={(e) => handlePreset(e.target.value as MissionDatePreset)}
          className={[
            'h-10 w-full rounded-md border border-surface-border bg-surface px-3 text-sm',
            'focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand',
          ].join(' ')}
        >
          {PRESETS.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </select>
      </div>

      {value.preset === 'custom' ? (
        <>
          <div className="sm:col-span-2 flex flex-col gap-1.5">
            <Label htmlFor={`${presetId}-from`}>Du</Label>
            <Input
              id={`${presetId}-from`}
              type="date"
              value={value.dateFrom}
              onChange={(e) => onChange({ ...value, dateFrom: e.target.value })}
            />
          </div>
          <div className="sm:col-span-2 flex flex-col gap-1.5">
            <Label htmlFor={`${presetId}-to`}>Au</Label>
            <Input
              id={`${presetId}-to`}
              type="date"
              value={value.dateTo}
              onChange={(e) => onChange({ ...value, dateTo: e.target.value })}
            />
          </div>
        </>
      ) : null}

      <div
        className={
          value.preset === 'custom'
            ? 'sm:col-span-2 flex flex-col gap-1.5'
            : 'sm:col-span-3 flex flex-col gap-1.5'
        }
      >
        <Label htmlFor={driverFieldId}>Chauffeur</Label>
        <SelectSearch
          triggerId={driverFieldId}
          placeholder="Tous"
          value={value.driverId}
          onChange={(v) => onChange({ ...value, driverId: v })}
          options={driverOptions}
          loading={driversLoading}
        />
      </div>

      <div
        className={
          value.preset === 'custom'
            ? 'sm:col-span-2 flex flex-col gap-1.5'
            : 'sm:col-span-2 flex flex-col gap-1.5'
        }
      >
        <Label htmlFor={clientFieldId}>Client</Label>
        <SelectSearch
          triggerId={clientFieldId}
          placeholder="Tous"
          value={value.clientId}
          onChange={(v) => onChange({ ...value, clientId: v })}
          options={clientOptions}
          loading={clientsLoading}
        />
      </div>

      <div className="sm:col-span-2 flex flex-col gap-1.5">
        <Label htmlFor={statusFieldId}>Statut</Label>
        <select
          id={statusFieldId}
          value={value.status}
          onChange={(e) =>
            onChange({ ...value, status: e.target.value as MissionStatusFilter })
          }
          className={[
            'h-10 w-full rounded-md border border-surface-border bg-surface px-3 text-sm',
            'focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand',
          ].join(' ')}
        >
          {STATUSES.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
      </div>

      <div
        className={
          value.preset === 'custom'
            ? 'sm:col-span-12 flex flex-col gap-1.5'
            : 'sm:col-span-2 flex flex-col gap-1.5'
        }
      >
        <Label htmlFor={searchId}>Recherche</Label>
        <div className="relative">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none"
            aria-hidden="true"
          />
          <Input
            id={searchId}
            type="search"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            placeholder="Chantier"
            className="pl-9"
          />
          {searchDraft !== '' ? (
            <button
              type="button"
              aria-label="Effacer"
              onClick={() => {
                setSearchDraft('')
                onChange({ ...value, search: '' })
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-text-muted hover:text-text hover:bg-surface-hover"
            >
              <X size={14} aria-hidden="true" />
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*  Date range presets                                                         */
/* -------------------------------------------------------------------------- */

interface DateRange {
  dateFrom: string
  dateTo: string
}

export function computeMissionRange(preset: MissionDatePreset): DateRange {
  const today = startOfLocalDay(new Date())

  switch (preset) {
    case 'today':
      return { dateFrom: localISO(today), dateTo: localISO(today) }
    case 'last7': {
      const from = addDays(today, -6)
      return { dateFrom: localISO(from), dateTo: localISO(today) }
    }
    case 'last30': {
      const from = addDays(today, -29)
      return { dateFrom: localISO(from), dateTo: localISO(today) }
    }
    case 'thisMonth': {
      const start = new Date(today.getFullYear(), today.getMonth(), 1)
      const end = new Date(today.getFullYear(), today.getMonth() + 1, 0)
      return { dateFrom: localISO(start), dateTo: localISO(end) }
    }
    case 'lastMonth': {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1)
      const end = new Date(today.getFullYear(), today.getMonth(), 0)
      return { dateFrom: localISO(start), dateTo: localISO(end) }
    }
    case 'custom':
    default:
      return { dateFrom: localISO(today), dateTo: localISO(today) }
  }
}

function startOfLocalDay(d: Date): Date {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d)
  x.setDate(x.getDate() + n)
  return x
}

function localISO(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}
